import type {
  ApplicationConfiguration,
  ApplicationConfigurationResponse,
  ApplicationVersion
} from '@/common/overmind/namespaces/arcgis/config/config.types';
import { UnexpectedStateError } from '@/overmind/errors';

import type { Api } from './apiFactory';
import type apiFactory from './apiFactory';

let api: Api;

export default {
  initialize(createApi: () => ReturnType<typeof apiFactory>) {
    api = createApi();
  },
  async getApplicationConfiguration(
    controller?: AbortController
  ): Promise<ApplicationConfiguration> {
    const appConfigUrl = '/app.config.json';
    const appVersionFileUrl = '/app.json';

    try {
      const [applicationConfigurationResponse, applicationVersion] =
        await Promise.allSettled([
          get<ApplicationConfigurationResponse>(appConfigUrl, controller),
          get<ApplicationVersion>(appVersionFileUrl, controller)
        ]);
      if (
        applicationConfigurationResponse.status === 'fulfilled' &&
        applicationVersion.status === 'fulfilled'
      ) {
        const sanitizedAppVersion = sanitizeAppVersion(
          applicationVersion.value.appVersion
        );
        return {
          ...applicationConfigurationResponse.value,
          appVersion: sanitizedAppVersion
            ? sanitizedAppVersion.toString()
            : applicationVersion.value.appCommit
        };
      } else {
        throw new GettingApplicationConfigurationFailed();
      }
    } catch (error) {
      throw new UnexpectedStateError();
    }
  }
};

function sanitizeAppVersion(
  value: ApplicationVersion['appVersion']
): Omit<ApplicationVersion['appVersion'], 'null'> | string {
  try {
    const version = JSON.parse(JSON.stringify(value) as string) as string | null;
    if (version === null || version === 'null' || version === undefined) {
      return null as unknown as Omit<ApplicationVersion['appVersion'], 'null'>;
    }
    if (version) {
      const versionWithoutHotfix = version.replace(/-hotfix$/, '');
      const versionDigits = versionWithoutHotfix.split('v');
      const [major, minor, patch, _] = versionDigits[1].split('.');
      return `v${major}.${minor}.${patch}`;
    }
    return version;
  } catch {
    return value as string;
  }
}

function get<T>(url: string, controller: AbortController | undefined) {
  return api.get<T>(
    url,
    undefined,
    controller ? { signal: controller.signal } : undefined
  );
}

export class GettingApplicationConfigurationFailed extends Error {
  constructor() {
    super('Unexpected state reached.');
  }
}
